import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import { Job, JobState, JobStatus, SortDirection } from '@/hooks/graphql/useAdminJobsGraphQL';
import { formatDistanceToNow } from 'date-fns';

interface JobQueueTableProps {
  jobs: Job[];
  loading: boolean;
  onRowClick: (jobId: string) => void;
  sortBy: string;
  sortOrder: SortDirection;
  onSort: (column: string) => void;
}

const columnHelper = createColumnHelper<Job>();

/**
 * Status Badge Component
 */
function StatusBadge({ status }: { status: JobStatus | null }) {
  if (!status) return <span className="text-gray-400 text-xs">—</span>;

  const colors: Record<JobStatus, string> = {
    [JobStatus.QUEUED]: 'bg-gray-100 text-gray-800',
    [JobStatus.DELAYED]: 'bg-yellow-100 text-yellow-800',
    [JobStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
    [JobStatus.RETRYING]: 'bg-orange-100 text-orange-800',
    [JobStatus.SUCCEEDED]: 'bg-green-100 text-green-800',
    [JobStatus.FAILED]: 'bg-red-100 text-red-800',
    [JobStatus.CANCELLED]: 'bg-gray-100 text-gray-600',
  };

  const labels: Record<JobStatus, string> = {
    [JobStatus.QUEUED]: 'Queued',
    [JobStatus.DELAYED]: 'Delayed',
    [JobStatus.IN_PROGRESS]: 'In Progress',
    [JobStatus.RETRYING]: 'Retrying',
    [JobStatus.SUCCEEDED]: 'Succeeded',
    [JobStatus.FAILED]: 'Failed',
    [JobStatus.CANCELLED]: 'Cancelled',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}
    >
      {labels[status]}
    </span>
  );
}

/**
 * State Badge Component
 */
function StateBadge({ state }: { state: JobState }) {
  const colors: Record<JobState, string> = {
    [JobState.CREATED]: 'bg-gray-100 text-gray-800',
    [JobState.RETRY]: 'bg-orange-100 text-orange-800',
    [JobState.ACTIVE]: 'bg-blue-100 text-blue-800',
    [JobState.COMPLETED]: 'bg-green-100 text-green-800',
    [JobState.FAILED]: 'bg-red-100 text-red-800',
    [JobState.CANCELLED]: 'bg-gray-100 text-gray-600',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[state]}`}
    >
      {state}
    </span>
  );
}

/**
 * Timestamp Cell Component
 */
function TimestampCell({ timestamp }: { timestamp: string | null }) {
  if (!timestamp) return <span className="text-gray-400 text-xs">—</span>;

  const date = new Date(timestamp);
  const relative = formatDistanceToNow(date, { addSuffix: true });

  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-900">{relative}</span>
      <span className="text-xs text-gray-500">{date.toLocaleString()}</span>
    </div>
  );
}

/**
 * Duration Cell Component
 */
function DurationCell({ duration }: { duration: number | null }) {
  if (!duration) return <span className="text-gray-400 text-xs">—</span>;

  const seconds = Math.floor(duration / 1000);
  if (seconds < 60) return <span className="text-xs">{seconds}s</span>;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return <span className="text-xs">{minutes}m {remainingSeconds}s</span>;
}

/**
 * Retry Count Cell Component
 */
function RetryCountCell({ job }: { job: Job }) {
  const hasRetries = job.retryCount > 0;
  const isRetrying = job.state === JobState.RETRY;

  return (
    <div className="flex flex-col">
      <span className={`text-xs ${hasRetries ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
        {job.retryCount} / {job.retryLimit}
      </span>
      {isRetrying && (
        <span className="text-xs text-orange-500">
          Retry in {job.retryDelay}s {job.retryBackoff && '(backoff)'}
        </span>
      )}
    </div>
  );
}

const columns: ColumnDef<Job, any>[] = [
  columnHelper.accessor('id', {
    header: 'Job ID',
    cell: (info) => (
      <span className="font-mono text-xs text-gray-600">{info.getValue().slice(0, 8)}...</span>
    ),
  }),
  columnHelper.accessor('name', {
    header: 'Queue',
    cell: (info) => <span className="text-sm font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => <StatusBadge status={info.getValue()} />,
  }),
  columnHelper.accessor('state', {
    header: 'State',
    cell: (info) => <StateBadge state={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'retries',
    header: 'Retries',
    cell: (info) => <RetryCountCell job={info.row.original} />,
  }),
  columnHelper.accessor('createdOn', {
    header: 'Created',
    cell: (info) => <TimestampCell timestamp={info.getValue()} />,
  }),
  columnHelper.accessor('startedOn', {
    header: 'Started',
    cell: (info) => <TimestampCell timestamp={info.getValue()} />,
  }),
  columnHelper.accessor('completedOn', {
    header: 'Completed',
    cell: (info) => <TimestampCell timestamp={info.getValue()} />,
  }),
  columnHelper.accessor('duration', {
    header: 'Duration',
    cell: (info) => <DurationCell duration={info.getValue()} />,
  }),
  columnHelper.accessor('priority', {
    header: 'Priority',
    cell: (info) => <span className="text-xs text-gray-600">{info.getValue()}</span>,
  }),
];

/**
 * Sort Indicator Component
 */
function SortIndicator({ column, sortBy, sortOrder }: { column: string; sortBy: string; sortOrder: SortDirection }) {
  if (sortBy !== column) {
    return <span className="ml-1 text-gray-400">↕</span>;
  }
  return <span className="ml-1 text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
}

/**
 * Job Queue Table Component
 * Feature: 073-job-queue-inspector
 *
 * Displays jobs from pg-boss queue using TanStack Table with sortable columns
 */
export function JobQueueTable({ jobs, loading, onRowClick, sortBy, sortOrder, onSort }: JobQueueTableProps) {
  const table = useReactTable({
    data: jobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && jobs.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center min-h-[400px] flex items-center justify-center">
        <p className="text-gray-500">Loading jobs...</p>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center min-h-[400px] flex items-center justify-center">
        <p className="text-gray-500">No jobs found</p>
      </div>
    );
  }

  // Map of column IDs to sortBy values
  const sortableColumns: Record<string, string> = {
    name: 'name',
    state: 'state',
    createdOn: 'createdOn',
    completedOn: 'completedOn',
    retries: 'retryCount',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  const isSortable = columnId in sortableColumns;
                  const sortValue = sortableColumns[columnId];

                  return (
                    <th
                      key={header.id}
                      onClick={() => isSortable && onSort(sortValue)}
                      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        isSortable ? 'cursor-pointer hover:bg-gray-100 transition-colors select-none' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {isSortable && (
                          <SortIndicator column={sortValue} sortBy={sortBy} sortOrder={sortOrder} />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowClick(row.original.id)}
                className="hover:bg-blue-50 transition-colors cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
